import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, ChevronDown, Home, Megaphone } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { ActiveJob } from '../lib/types';
import { JobStatusIcon } from '../lib/jobs';
import { titleCaseSnake } from '../lib/text';
import { useTopMenus } from './topMenus';
import { hasUnseenReleases } from '../lib/releaseNotes';
import { useAgentCard } from './AgentDock';
import NotificationBell from './NotificationBell';

function ActiveJobsWidget({ jobs }: { jobs: ActiveJob[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (jobs.length <= 1) return;
    const interval = setInterval(() => setIndex(i => (i + 1) % jobs.length), 5000);
    return () => clearInterval(interval);
  }, [jobs.length]);

  // Clamp the visible index if the queue shrinks (a job just completed).
  useEffect(() => {
    if (index >= jobs.length && jobs.length > 0) setIndex(0);
  }, [jobs.length, index]);

  if (jobs.length === 0) return null;
  const visible = jobs[Math.min(index, jobs.length - 1)];
  const label = visible.task_type ? titleCaseSnake(visible.task_type) : 'Job';

  return (
    <div className="flex items-center min-w-0">
      <Link
        to="/jobs"
        title={visible.source ? `${label} · ${visible.source}` : label}
        className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white text-navy shadow-sm hover:bg-offwhite transition-colors min-w-0"
      >
        <JobStatusIcon status={visible.status} className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate max-w-48 min-w-0">{label}</span>
        {visible.source && (
          <span className="text-graphite/70 truncate max-w-48 shrink min-w-0">· {visible.source}</span>
        )}
        {jobs.length > 1 && (
          <span className="text-[10px] font-mono text-graphite/70 shrink-0">
            {Math.min(index, jobs.length - 1) + 1}/{jobs.length}
          </span>
        )}
      </Link>
    </div>
  );
}

// Shared open-menu state, so hovering another trigger moves the open menu across like a desktop menu bar.
const MenuBarContext = createContext<{
  openLabel: string | null;
  setOpenLabel: (label: string | null) => void;
}>({ openLabel: null, setOpenLabel: () => {} });

function Dropdown({ label, children }: { label: string; children: ReactNode }) {
  const { openLabel, setOpenLabel } = useContext(MenuBarContext);
  const open = openLabel === label;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpenLabel(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenLabel(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, setOpenLabel]);

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => { if (openLabel !== null && !open) setOpenLabel(label); }}
    >
      <button
        onClick={() => setOpenLabel(open ? null : label)}
        aria-expanded={open}
        className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-offwhite transition-colors ${open ? 'bg-white/15' : 'hover:bg-white/10'}`}
      >
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          onClick={() => setOpenLabel(null)}
          className="absolute top-full left-0 mt-2 navaris-dropdown min-w-[240px] max-w-[280px] max-h-[70vh] overflow-y-auto z-40"
        >
          {children}
        </div>
      )}
    </div>
  );
}

const ITEM_PAD = ['pl-4', 'pl-8', 'pl-12'] as const;

function MenuItem({
  to, onSelect, depth = 0, icon, hint, muted = false, title, children,
}: {
  to?: string;
  onSelect?: () => void;
  depth?: 0 | 1 | 2;
  icon?: ReactNode;
  hint?: ReactNode;
  muted?: boolean;
  title?: string;
  children: ReactNode;
}) {
  const pad = ITEM_PAD[depth];
  const className = `w-full flex items-center gap-2 ${pad} pr-4 py-1.5 text-left text-sm text-graphite hover:bg-offwhite hover:text-brand-dark transition-colors ${muted ? 'opacity-50' : ''}`;
  const body = (
    <>
      {icon && <span className="shrink-0 flex">{icon}</span>}
      <span className="truncate">{children}</span>
      {hint && <span className="ml-auto shrink-0">{hint}</span>}
    </>
  );
  return to ? (
    <Link to={to} onClick={onSelect} title={title} className={className}>{body}</Link>
  ) : (
    <button type="button" onClick={onSelect} title={title} className={className}>{body}</button>
  );
}

function MenuDivider() {
  return <div className="my-1 border-t border-softgrey/60" />;
}

// Re-checked on every navigation, so the dot clears once the person has visited /whats-new.
function WhatsNewButton() {
  const location = useLocation();
  const { resetCard } = useAgentCard();
  const [unseen, setUnseen] = useState(hasUnseenReleases);
  useEffect(() => { setUnseen(hasUnseenReleases()); }, [location.pathname]);
  const label = unseen ? "What's New - new updates" : "What's New";
  return (
    <Link
      to="/whats-new"
      onClick={resetCard}
      title={label}
      aria-label={label}
      className="relative w-8 h-8 rounded-md flex items-center justify-center text-offwhite hover:bg-white/10 transition-colors shrink-0"
    >
      <Megaphone className="w-[18px] h-[18px]" />
      {unseen && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-brand ring-2 ring-navy" />}
    </Link>
  );
}

interface HeaderProps {
  onMenuOpen: () => void;
}

export default function Header({ onMenuOpen }: HeaderProps) {
  const { user } = useAuth();
  const { activeJobs } = useWorkspace();
  const menus = useTopMenus();
  const { resetCard } = useAgentCard();
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  if (!user) return null;

  const userInitials = user.full_name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '??';

  return (
    <header className="col-span-full h-12 px-3 flex items-center gap-2 bg-navy text-offwhite relative z-30 shadow-[0_2px_8px_rgba(16,40,48,0.3)]">
      <button
        onClick={onMenuOpen}
        className="lg:hidden w-8 h-8 rounded-md flex items-center justify-center text-offwhite hover:bg-white/10 transition-colors shrink-0"
        aria-label="Open menu"
        title="Open menu"
      >
        <Menu className="w-5 h-5" />
      </button>

      <MenuBarContext.Provider value={{ openLabel: openMenu, setOpenLabel: setOpenMenu }}>
      <Link
        to="/"
        onClick={resetCard}
        title="Workspace home"
        aria-label="Workspace home"
        className="hidden lg:flex w-8 h-8 rounded-md items-center justify-center text-offwhite hover:bg-white/10 transition-colors shrink-0"
      >
        <Home className="w-5 h-5" />
      </Link>
      <nav className="hidden lg:flex items-center gap-1 shrink-0">
        {menus.map(group => (
          <Dropdown key={group.label} label={group.label}>
            {group.entries.map((e, i) =>
              e.kind === 'divider'
                ? <MenuDivider key={`d${i}`} />
                : (
                  <MenuItem key={e.key ?? e.to} to={e.to} onSelect={e.onSelect} depth={e.depth} icon={e.icon} hint={e.hint} muted={e.muted} title={e.title}>
                    {e.label}
                  </MenuItem>
                ),
            )}
          </Dropdown>
        ))}
      </nav>
      </MenuBarContext.Provider>

      {/* Running jobs take whatever the bar has left, truncating. */}
      <div className="flex-1 min-w-0 flex items-center justify-center">
        <ActiveJobsWidget jobs={activeJobs} />
      </div>

      <WhatsNewButton />
      <NotificationBell />
      <Link
        to="/profile"
        onClick={resetCard}
        className="flex items-center gap-2.5 hover:opacity-90 transition-opacity shrink-0"
        title={user.full_name}
      >
        <span className="text-offwhite/90 text-sm hidden xl:inline">{user.full_name}</span>
        <div className="w-8 h-8 rounded-full bg-white/15 border border-white/20 flex items-center justify-center text-offwhite text-xs font-bold">
          {userInitials}
        </div>
      </Link>
    </header>
  );
}
